import {
  Button,
  Heading,
  InfoListItem,
  ListContainer,
  Typography,
} from '@altify/components';
import {
  formatAssetAmount,
  getDisplayAssetCode,
  useContent,
} from '@altify/utils';
import RewardTierIcon from 'app/_authenticated/trade/assets/RewardTierIcon';
import CurrencyIcon from 'app/_authenticated/trade/components/CurrencyIcon/CurrencyIcon';
import { DialogOrBottomSheet } from 'components/DialogOrBottomSheet/DialogOrBottomSheet';
import { useEffect, useMemo, useState } from 'react';
import { useRefreshQuoteMutation } from 'store/slices/trade-api';
import { tradeSharedContent } from '../../tradeShared.content';
import { content } from './ReviewOrderModal.content';

const ReviewOrderModal = ({
  type,
  isOpen,
  orderId,
  fromProductCode,
  toProductCode,
  fromAmount,
  toAmount,
  price,
  fee,
  rewardTierName,
  rewardTierCode,
  expiresAt,
  currentTime,
  onClose,
  onInfoIconPressed,
  onSubmit,
}: {
  onInfoIconPressed: (
    infoType: 'tradePrice' | 'tradingFee' | 'smartTrade' | 'rewardLevel',
  ) => void;
  type: 'buy' | 'sell';
  orderId: string;
  isOpen: boolean;
  fromProductCode: string;
  toProductCode: string;
  fromAmount: number;
  toAmount: number;
  fee: number;
  rewardTierName: string;
  rewardTierCode: string;
  price: number;
  expiresAt: string;
  currentTime: string;
  onClose: () => void;
  onSubmit: () => void;
}) => {
  const localizedContent = useContent(content, tradeSharedContent);

  const [refreshQuote, refreshQuoteResult] = useRefreshQuoteMutation();
  const diffInSeconds = getDifferenceInSeconds(expiresAt, currentTime);
  const [refreshCountdown, setRefreshCountdown] = useState(diffInSeconds);
  const needsRefreshing = refreshCountdown === 0;

  const rewardIconComponent = useMemo(
    () =>
      ({ className }: { className?: string }) => (
        <RewardTierIcon rewardTierCode={rewardTierCode} className="-my-4" />
      ),
    [rewardTierCode],
  );

  const listItems = {
    buy: [
      {
        id: 1,
        title: formatAssetAmount(fromAmount, fromProductCode ?? '', {
          showSpacer: true,
        }),
        subtitle: localizedContent.youAreUsing,
      } as const,
      {
        id: 2,
        title: `1 ${toProductCode?.toLocaleUpperCase()} = ${formatAssetAmount(
          price,
          fromProductCode ?? '',
          { showSpacer: true },
        )}`,
        subtitle: localizedContent.tradePrice,
        infoIconVisible: true,
        onInfoIconPress: () => onInfoIconPressed('tradePrice'),
      } as const,
      {
        id: 3,
        title: rewardTierName,
        onInfoIconPress: () => onInfoIconPressed('rewardLevel'),
        subtitle: localizedContent.rewardLevel,
        infoIconVisible: true,
        iconComponent: rewardIconComponent,
      } as const,
      {
        id: 4,
        title: formatAssetAmount(fee, fromProductCode ?? '', {
          showSpacer: true,
        }),
        subtitle: localizedContent.tradingFee,
        onInfoIconPress: () => onInfoIconPressed('tradingFee'),
        infoIconVisible: true,
      } as const,
      {
        id: 5,
        title: localizedContent.yes,
        subtitle: localizedContent.smartTrade,
        onInfoIconPress: () => onInfoIconPressed('smartTrade'),
        infoIconVisible: true,
      } as const,
      {
        id: 6,
        title: localizedContent.buyNow,
        subtitle: localizedContent.orderType,
        infoIconVisible: false,
      } as const,
    ],
    sell: [
      {
        id: 1,
        title: formatAssetAmount(fromAmount, fromProductCode ?? '', {
          showSpacer: true,
        }),
        subtitle: localizedContent.youAreUsing,
      } as const,
      {
        id: 2,
        title: `1 ${fromProductCode?.toLocaleUpperCase()} = ${formatAssetAmount(
          price,
          toProductCode ?? '',
          { showSpacer: true },
        )}`,
        subtitle: localizedContent.tradePrice,
        onInfoIconPress: () => onInfoIconPressed('tradePrice'),
        infoIconVisible: true,
      } as const,
      {
        id: 3,
        title: rewardTierName,
        subtitle: localizedContent.rewardLevel,
        onInfoIconPress: () => onInfoIconPressed('rewardLevel'),
        infoIconVisible: true,
        iconComponent: rewardIconComponent,
      } as const,
      {
        id: 4,
        title: formatAssetAmount(fee, fromProductCode ?? '', {
          showSpacer: true,
        }),
        subtitle: localizedContent.tradingFee,
        onInfoIconPress: () => onInfoIconPressed('tradingFee'),
        infoIconVisible: true,
      } as const,
      {
        id: 5,
        title: localizedContent.yes,
        subtitle: localizedContent.smartTrade,
        onInfoIconPress: () => onInfoIconPressed('smartTrade'),
        infoIconVisible: true,
      } as const,
      {
        id: 6,
        title: localizedContent.sellNow,
        subtitle: localizedContent.orderType,
        infoIconVisible: false,
      } as const,
    ],
    recurring: [],
  };

  const titles = {
    buy: localizedContent.youAreBuying,
    sell: localizedContent.youAreReceiving,
  };

  const amounts = {
    buy: formatAssetAmount(toAmount, toProductCode ?? '', { showSpacer: true }),
    sell: formatAssetAmount(toAmount, toProductCode ?? '', {
      showSpacer: true,
    }),
  };

  async function handleSubmit() {
    if (needsRefreshing) {
      await refreshQuote({ orderId: orderId, type: type });
    } else {
      onSubmit();
      onClose?.();
    }
  }

  function getDifferenceInSeconds(expirationTime: string, now: string) {
    const expires = new Date(expirationTime);
    const current = new Date(now);

    const diffInSeconds = Math.round(
      (expires.getTime() - current.getTime()) / 1000,
    );
    return diffInSeconds;
  }

  useEffect(() => {
    if (expiresAt && currentTime) {
      const diffInSeconds = getDifferenceInSeconds(expiresAt, currentTime);
      if (diffInSeconds) setRefreshCountdown(diffInSeconds);
    }
  }, [expiresAt, currentTime]);

  useEffect(() => {
    if (refreshCountdown > 0 && !refreshQuoteResult.isLoading) {
      setTimeout(() => {
        setRefreshCountdown(refreshCountdown - 1);
      }, 1000);
    }
  }, [refreshCountdown, refreshQuoteResult.isLoading]);

  useEffect(() => {
    if (!refreshQuoteResult.isLoading) {
      const diffInSeconds = getDifferenceInSeconds(
        refreshQuoteResult?.data?.expiresAt!,
        refreshQuoteResult?.data?.currentTime!,
      );

      if (diffInSeconds) setRefreshCountdown(diffInSeconds);
    }
  }, [refreshQuoteResult]);

  const list = listItems[type];
  const title = titles[type];
  const amount = amounts[type];

  return (
    <DialogOrBottomSheet
      blurEnabled={true}
      isOpen={isOpen}
      onClose={() => onClose?.()}
      backgroundColor="#0C1C2C"
      footerContent={
        <div className="px-4">
          <Button
            size="large"
            color="primary"
            loading={refreshQuoteResult.isLoading}
            text={
              !needsRefreshing
                ? type == 'buy'
                  ? localizedContent.confirmBuy
                  : localizedContent.confirmSale
                : localizedContent.refreshQuote
            }
            className="my-4 w-full"
            onPress={handleSubmit}
          />
        </div>
      }>
      <div className="flex flex-col w-full items-center px-4 shrink-0">
        <div>
          <Heading level={5}>{localizedContent.confirmDetails}</Heading>
        </div>
        <CurrencyIcon size={48} code={toProductCode} className="my-4" />
        <Typography size="large" className="text-neutral-300">
          {title}
        </Typography>
        <Heading level={2} className="mb-10">
          {amount}
        </Heading>
        <ListContainer>
          {list.map(i => (
            <InfoListItem {...i} key={i.id} />
          ))}
        </ListContainer>
        {expiresAt && currentTime && !refreshQuoteResult.error && (
          <div className="mt-6">
            <Typography size="small">
              {!needsRefreshing
                ? localizedContent.secureTradePriceWithin
                : localizedContent.quoteExpired(
                    getDisplayAssetCode(
                      type == 'buy' ? toProductCode : fromProductCode,
                    )!,
                  )}
              <Typography size="small" weight="bold">
                {!needsRefreshing ? ` ${refreshCountdown}s` : ''}
              </Typography>
            </Typography>
          </div>
        )}
        {refreshQuoteResult.error && (
          <Typography size="small" className="text-error">
            {localizedContent.tradeError}
          </Typography>
        )}
      </div>
    </DialogOrBottomSheet>
  );
};

export default ReviewOrderModal;
